import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Pause, Play, RotateCcw, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBarRacePlayer, type BarRacePlayer } from '../hooks/useBarRacePlayer';
import { RACE_SPEEDS, barPercent, formatElapsed, rankBars, timelineTicks, toPlaybackPoints, votesAt } from '../utils/barRace';
import type { RaceTimeline } from '../types';
import { MinisterPhoto } from './MinisterPhoto';

const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5', '#0d9488', '#be123c'];

export interface RaceCandidate {
  id: string;
  name: string;
  photo: string | null;
  /** Color slot: the candidate's place in the election, so the color is the same in every round */
  colorIndex: number;
}

interface BarChartRaceProps {
  candidates: RaceCandidate[];
  timeline: RaceTimeline;
  /** Votes needed for 100 % of the bar (eligible or valid votes, as the round defines) */
  baseCount: number;
  thresholdPct: number;
  /** Winner of the round, highlighted at the end */
  winnerId?: string | null;
  /** Replay length at 1× */
  playbackMs?: number;
  /** Bigger type for the projector */
  large?: boolean;
  /** Play / pause / replay, speed and timeline slider (admin). Off on the projector. */
  controls?: boolean;
  /** Playback driven from outside (projection screen keyboard); otherwise the chart owns its player */
  player?: BarRacePlayer;
  blankVotes?: number;
}

/**
 * The round replayed: bars grow as the votes came in, ordered by votes, against the percentage base of
 * the round with a line at the winning threshold; a big light clock and running total in the corner,
 * and a timeline with marks under the bars. Ends on the final result.
 */
export function BarChartRace({
  candidates, timeline, baseCount, thresholdPct, winnerId, playbackMs = 14000, large = false, controls = true, player, blankVotes = 0,
}: BarChartRaceProps) {
  const points = useMemo(() => toPlaybackPoints(timeline.points), [timeline]);
  const own = useBarRacePlayer({ playbackMs, ready: points.length > 0, resetKey: timeline.from });
  const pl = player ?? own;
  const ids = useMemo(() => candidates.map((c) => c.id), [candidates]);
  const byId = useMemo(() => new Map(candidates.map((c) => [c.id, { ...c, color: COLORS[c.colorIndex % COLORS.length] }])), [candidates]);

  const tStart = new Date(timeline.from).getTime();
  const tEnd = Math.max(new Date(timeline.to).getTime(), tStart + 1);
  const progress = points.length === 0 ? 1 : pl.progress;
  const playhead = tStart + progress * (tEnd - tStart);
  const bars = useMemo(() => rankBars(votesAt(points, ids, playhead)), [points, ids, playhead]);
  const maxVotes = Math.max(1, ...bars.map((b) => b.votes));
  const finished = progress >= 1;
  const total = bars.reduce((s, b) => s + b.votes, 0);
  const ticks = useMemo(() => timelineTicks(tEnd - tStart), [tStart, tEnd]);
  const elapsed = formatElapsed(((playhead - tStart) / 1000));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={cn('font-black tabular-nums', large ? 'text-5xl' : 'text-3xl')}>
            {total.toLocaleString('es-BO')} <span className={cn('font-medium opacity-70', large ? 'text-2xl' : 'text-lg')}>votos por candidatos</span>
          </p>
          {finished && blankVotes > 0 && <p className={cn('opacity-70', large ? 'text-2xl' : 'text-sm')}>{blankVotes} en blanco</p>}
        </div>
        {/* Big light clock of the round, like the year in a bar race */}
        <p className={cn('font-black tabular-nums leading-none opacity-25', large ? 'text-8xl' : 'text-5xl')} aria-label="Tiempo transcurrido">{elapsed}</p>
      </div>

      <div className={cn('space-y-3', large && 'space-y-4')}>
        {bars.map((b, i) => {
          const c = byId.get(b.id);
          if (!c) return null;
          const pct = barPercent(b.votes, baseCount, maxVotes);
          const isWinner = finished && winnerId === b.id;
          return (
            <motion.div key={b.id} layout transition={{ type: 'spring', stiffness: 320, damping: 32 }} className="flex items-center gap-3">
              <span className={cn('w-8 shrink-0 text-right font-bold tabular-nums opacity-60', large ? 'text-3xl' : 'text-lg')}>{i + 1}</span>
              <MinisterPhoto filename={c.photo} name={c.name} className={large ? 'size-20 border-4' : 'size-12 border-2'} />
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between gap-3">
                  <span className={cn('flex items-center gap-2 truncate font-semibold', large ? 'text-3xl' : 'text-sm')}>
                    {c.name}
                    {isWinner && <Trophy className={cn('shrink-0 text-amber-400', large ? 'size-8' : 'size-4')} />}
                  </span>
                  <span className={cn('shrink-0 font-bold tabular-nums', large ? 'text-4xl' : 'text-lg')} style={{ color: c.color }}>
                    {b.votes} <span className="font-medium opacity-70">· {pct.toFixed(1)} %</span>
                  </span>
                </div>
                <div className={cn('relative w-full overflow-hidden rounded-md bg-black/10 dark:bg-white/10', large ? 'h-12' : 'h-7')}>
                  <motion.div
                    className="h-full rounded-md"
                    style={{ backgroundColor: c.color }}
                    animate={{ width: `${pct}%` }}
                    transition={{ ease: 'linear', duration: 0.12 }}
                  />
                  <div
                    className="absolute inset-y-0 border-l-2 border-dashed border-foreground/70"
                    style={{ left: `${Math.min(100, thresholdPct)}%` }}
                    title={`${thresholdPct} % para ganar`}
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      <p className={cn('text-right opacity-60', large ? 'text-xl' : 'text-xs')}>Línea punteada: {thresholdPct} % para ganar</p>

      {points.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            {controls && (
              <button
                type="button"
                onClick={() => (pl.finished ? pl.restart() : pl.toggle())}
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow hover:bg-primary/90"
                aria-label={pl.finished ? 'Repetir' : pl.playing ? 'Pausar' : 'Reproducir'}
              >
                {pl.finished ? <RotateCcw className="size-5" /> : pl.playing ? <Pause className="size-5" /> : <Play className="size-5" />}
              </button>
            )}
            <div className="relative flex-1">
              {/* Progress track (and slider when the controls are on) */}
              <div className={cn('relative rounded-full bg-black/10 dark:bg-white/10', large ? 'h-2' : 'h-1.5')}>
                <div className="h-full rounded-full bg-primary" style={{ width: `${progress * 100}%` }} />
                <div className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary" style={{ left: `${progress * 100}%` }} />
              </div>
              {controls && (
                <input
                  type="range"
                  min={0}
                  max={1000}
                  value={Math.round(progress * 1000)}
                  onChange={(e) => pl.seek(Number(e.target.value) / 1000)}
                  className="absolute inset-x-0 -top-2 h-6 w-full cursor-pointer opacity-0"
                  aria-label="Línea de tiempo de la votación"
                />
              )}
              <div className="relative mt-1 h-4">
                {ticks.map((t) => (
                  <span
                    key={t.pct}
                    className={cn('absolute -translate-x-1/2 tabular-nums opacity-50', large ? 'text-base' : 'text-[10px]')}
                    style={{ left: `${t.pct}%` }}
                  >
                    {t.label}
                  </span>
                ))}
              </div>
            </div>
            {controls && (
              <div className="flex shrink-0 items-center gap-1">
                {RACE_SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => pl.setSpeed(s)}
                    className={cn('rounded-md px-2 py-1 text-xs font-medium', pl.speed === s ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted')}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
